/*
  Error model for the API client.

  The server speaks application/problem+json (RFC 9457), which the Go side builds in
  internal/httpx/render. Everything the console needs to branch on is either the HTTP
  status or the problem `type`, so this module turns both into one discriminated union and
  nothing downstream touches a raw Response.

  Two rules encoded here rather than left to each call site:

  1. Authentication failures are deliberately coarse. docs/07-identity-rbac.md I.1 requires
     that lockout responses be uniform so they do not confirm account existence. The server
     therefore returns the same problem type for a wrong password, an unknown email, and a
     disabled account. This client does not try to be cleverer than that, and the login UI
     renders one message for all of them.
  2. A transport failure is its own kind, not a status 0. "Could not reach the server" and
     "the server said no" need different copy and different retry behaviour.
*/

export type ApiErrorKind =
  /** Network, DNS, TLS, or an aborted request. No HTTP response was received. */
  | 'network'
  /** Email or password rejected. Intentionally indistinguishable from an unknown account. */
  | 'invalid_credentials'
  /** Account locked by the backoff policy. Also returned without confirming existence. */
  | 'account_locked'
  /** Password accepted, a second factor is now required. Carries an mfaToken. */
  | 'mfa_required'
  /** The submitted TOTP code was wrong, reused, or outside the drift window. */
  | 'mfa_invalid'
  /** Valid credentials, but the session needs a fresh MFA assertion for this action. */
  | 'step_up_required'
  /** 401 on a normal call: the access token is missing, expired, or revoked. */
  | 'unauthenticated'
  /** 403: in tenant, permission missing. Cross-tenant is a 404 by design. */
  | 'forbidden'
  | 'not_found'
  /** 409 on a write that lost a race. */
  | 'conflict'
  /** 422 with per-field messages. */
  | 'validation'
  /** 429, with a Retry-After if the server sent one. */
  | 'rate_limited'
  /** 5xx. */
  | 'server'
  /** Anything this client did not anticipate. */
  | 'unknown';

export interface ProblemDetails {
  readonly type?: string;
  readonly title?: string;
  readonly detail?: string;
  readonly status?: number;
  readonly errors?: Readonly<Record<string, readonly string[]>>;
  /** Opaque continuation token for the MFA step. Short lived, single use. */
  readonly mfaToken?: string;
  readonly retryAfterSeconds?: number;
  /** Set when the account lock has a known expiry the UI may show. */
  readonly lockedUntil?: string;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(kind: ApiErrorKind, status: number, problem: ProblemDetails, message?: string) {
    super(message ?? problem.title ?? kind);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.problem = problem;
  }

  get fieldErrors(): Readonly<Record<string, readonly string[]>> {
    return this.problem.errors ?? {};
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Problem `type` suffixes the Go side emits, mapped to a client kind. */
const PROBLEM_TYPE_TO_KIND: Readonly<Record<string, ApiErrorKind>> = {
  'invalid-credentials': 'invalid_credentials',
  'account-locked': 'account_locked',
  'mfa-required': 'mfa_required',
  'mfa-invalid': 'mfa_invalid',
  'step-up-required': 'step_up_required',
};

export function kindFromResponse(status: number, problem: ProblemDetails): ApiErrorKind {
  const type = problem.type ?? '';
  for (const [suffix, kind] of Object.entries(PROBLEM_TYPE_TO_KIND)) {
    if (type.endsWith(suffix)) {
      return kind;
    }
  }
  if (status === 401) {
    return 'unauthenticated';
  }
  if (status === 403) {
    return 'forbidden';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (status === 422) {
    return 'validation';
  }
  if (status === 429) {
    return 'rate_limited';
  }
  if (status >= 500) {
    return 'server';
  }
  return 'unknown';
}

export function networkError(cause: unknown): ApiError {
  return new ApiError(
    'network',
    0,
    { title: 'Could not reach the JIT server' },
    cause instanceof Error ? cause.message : 'Network request failed',
  );
}
